/**
 * Erst-Admin-Adresse eines Mandanten anzeigen und nachziehen.
 *
 *   PUT /api/tenants/:slug/admin-email { adminEmail }
 *
 * Der Server laesst nur Adressen in der Mandanten-Maildomain zu, die im
 * Realm bereits Mitglied von /tenant-admin sind (Keycloak wird nicht
 * geaendert). Der Wert speist den Zugangsdaten-Reset -- deshalb muss er
 * zum tatsaechlichen Anmeldenamen passen.
 */
import { useState } from 'react';
import { extractApiError, updateTenantAdminEmail } from '../services/api';

interface Props {
  slug: string;
  current: string;
  isReadOnly: boolean;
  onChanged: () => void;
}

export function AdminEmailInline({ slug, current, isReadOnly, onChanged }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setBusy(true); setErr(null);
    try {
      await updateTenantAdminEmail(slug, value.trim().toLowerCase());
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!editing) {
    return (
      <p className="text-sm text-slate-500">
        Admin: {current}
        {!isReadOnly && (
          <button
            type="button"
            className="ml-2 text-xs text-blue-700 underline"
            onClick={() => { setValue(current); setErr(null); setEditing(true); }}
          >
            ändern
          </button>
        )}
      </p>
    );
  }

  return (
    <div className="text-sm text-slate-500">
      <label className="block">
        Admin:
        <input
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="ml-2 px-2 py-0.5 border rounded font-mono text-xs w-72"
          disabled={busy}
        />
      </label>
      <div className="mt-1 flex gap-2">
        <button type="button" className="btn-primary text-xs" onClick={save} disabled={busy || value.trim() === ''}>
          Speichern
        </button>
        <button type="button" className="text-xs underline" onClick={() => setEditing(false)} disabled={busy}>
          Abbrechen
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-1">
        Nur eine Adresse in der Mandantendomain, die im Realm schon Mitglied von <code>/tenant-admin</code> ist.
      </p>
      {err && <p className="text-xs text-red-700 mt-1">{err}</p>}
    </div>
  );
}
